import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { AiToolset, Api } from 'actions';

import {
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Popup,
} from 'semantic-ui-react';
import cn from 'classnames';
import { withRouter } from 'react-router-dom';
import Fade from 'react-reveal/Fade';
import { Segment, FileSelectLarge, ButtonFilled } from 'components';
import { colors } from 'utils/colors';
import * as Alert from 'actions/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// eslint-disable-next-line import/extensions
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import LogoSpinnerUpload from './LogoSpinnerUpload';

@withRouter
class UploadStatement extends Component {
  constructor(props) {
    super(props);
    const { account } = this.props;
    this.state = {
      institution: account.user_institution || '',
      accountNumber: (account && account.account_number) || '',
      accountName: account.label || '',
      currency: account.currency || 'CAD',
      selectedFile: {},
      errors: {
        institution: false,
        accountNumber: false,
        accountName: false,
        statementFile: false,
      },
      sending: false,
      isInfoOpen: false,
    };
    this.setSelectedFile = this.setSelectedFile.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, error, loading, pollingStatus } = this.props;
    if (prevProps.loading !== loading && !loading) {
      if (error.length !== 0) {
        if (process.env.NODE_ENV === 'development') {
          console.error(error);
        }
        if (pollingStatus === 'timeout') {
          dispatch(
            Alert.show({
              type: 'error',
              msg: 'Statement parsing timed out. Please try again later.',
            })
          );
        } else {
          dispatch(
            Alert.show({
              type: 'error',
              msg: 'Parsing Error. Please ensure your account details match the selected statement and try again.',
            })
          );
        }
      } else {
        dispatch(
          Alert.show({
            type: 'success',
            msg: 'Statement upload successful.',
          })
        );
      }
    }
    if (prevProps.error !== error) {
      this.render();
    }
  }

  setSelectedFile(selectedFile) {
    const { dispatch } = this.props;
    const { errors } = this.state;

    if (selectedFile.size > 5242880) {
      dispatch(
        Alert.show({
          type: 'error',
          msg: 'File is too large. Max File Size: 5MB',
        })
      );
      this.setState({ errors: { ...errors, statementFile: 'too large' } });
    } else {
      this.setState({ selectedFile });
    }
  }

  handleChange = (e, { name, value }) => {
    let processedValue = value;

    if (name === 'accountNumber') {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '');
    }

    let error = processedValue.length === 0;
    if (name === 'institution' && processedValue.length > 128) {
      error = `Character Limit Exceeded (${processedValue.length})`;
    } else if (
      (name === 'accountName' || name === 'accountNumber') &&
      processedValue.length > 32
    ) {
      error = `Character Limit Exceeded (${processedValue.length})`;
    }

    this.setState({ [name]: processedValue });
    this.setState({ errors: { [name]: error } });
  };

  submit = () => {
    const { dispatch, history, account } = this.props;
    const { institution, accountName, accountNumber, currency, selectedFile } =
      this.state;
    this.setState({ sending: true });

    const formData = new FormData();
    formData.append('institution', institution);
    formData.append('account_name', accountName);
    formData.append('account_number', accountNumber);
    formData.append('account_currency', currency);
    formData.append('statement_file', selectedFile);

    if (account && account.id && account.provider !== 'uld') {
      dispatch(
        Api.request('put', `/accounts/${account.id}/`, {
          body: { account_number: accountNumber },
        })
      ).then(() => {
        dispatch(AiToolset.sendStatement(formData)).then((taskID) => {
          this.setState({ sending: false });
          return dispatch(AiToolset.pollTaskStatus(taskID, history));
        });
      });
    } else {
      dispatch(AiToolset.sendStatement(formData)).then((taskID) => {
        this.setState({ sending: false });
        return dispatch(AiToolset.pollTaskStatus(taskID, history));
      });
    }
  };

  isSubmitButtonDisabled = () => {
    const { institution, accountNumber, accountName, selectedFile } =
      this.state;
    return (
      institution === '' ||
      accountNumber === '' ||
      accountName === '' ||
      !selectedFile.name
    );
  };

  render() {
    const {
      institution,
      accountNumber,
      accountName,
      currency,
      errors,
      selectedFile,
      sending,
      isInfoOpen,
    } = this.state;

    const { loading, account, pdfDown } = this.props;

    const isAccount = Object.keys(account).length > 0;

    if (sending || loading) {
      return <LogoSpinnerUpload active />;
    }
    return (
      <div>
        <Segment margin="2rem 3rem 2rem 2rem">
          <div style={{ maxWidth: '1200px', margin: 'auto' }}>
            <StyledHeader>
              <Header size="large" style={{ margin: 0 }}>
                AI Upload
                <Header.Subheader>
                  Upload a statement or screenshot to add an account
                  automatically.
                </Header.Subheader>
              </Header>
            </StyledHeader>
            <Divider />
            <StyledDiv>
              <Fade>
                <div>
                  <Content>
                    <CollapseWrapper
                      open={isInfoOpen}
                      onClick={() =>
                        this.setState({
                          isInfoOpen: !isInfoOpen,
                        })
                      }
                    >
                      <Header
                        size="small"
                        style={{
                          top: isInfoOpen ? '-11px' : '6px',
                          left: '.5rem',
                          transition: 'all 300ms ease',
                          padding: '5px .75rem 0 .5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: isInfoOpen ? '-13px' : 0,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              background: isInfoOpen ? 'white' : 'transparent',
                            }}
                          >
                            <StyledButton
                              onClick={() =>
                                this.setState({
                                  isInfoOpen: !isInfoOpen,
                                })
                              }
                              isOpen={isInfoOpen}
                            >
                              {isInfoOpen ? '-' : '+'}
                            </StyledButton>
                            Instructions:
                          </div>
                          <FontAwesomeIcon
                            icon={faAngleDown}
                            style={{
                              marginLeft: '0.5rem',
                              transition: 'transform 300ms ease',
                              transform: isInfoOpen
                                ? 'rotate(-180deg)'
                                : 'rotate(0deg)',
                              background: isInfoOpen ? 'white' : 'transparent',
                            }}
                          />
                        </div>
                      </Header>
                      <Assumptions>
                        <ul>
                          {' '}
                          <li>
                            We use OpenAI to process the file. The file is
                            immediately deleted when we receive the data.
                          </li>
                          <li>
                            <b>Dual currency accounts:</b> Upload them as two
                            separate accounts (USD and CAD). Group them to
                            analyze.
                          </li>
                          <li>
                            <b>Mutual fund statements:</b> Some show only
                            abbreviated fund names and no fund codes. We suggest
                            you manually enter these accounts to choose the
                            correct funds/share classes.
                          </li>
                          <li>
                            Statements with multiple accounts and too many pages
                            may fail to parse. If that happens, use a screenshot
                            instead.
                          </li>
                          <li>
                            When uploading is complete, you will be re-directed
                            to the "Edit Account" view to review and save the
                            data.
                          </li>
                          <li>
                            <b>Scanned</b> PDFs are not text-based and therefore
                            cannot be read by the model. Download your statement
                            from your broker's website, or take a screenshot of
                            the holdings and upload as an image.
                          </li>
                        </ul>
                      </Assumptions>
                    </CollapseWrapper>
                    <FormGroup>
                      <div className="column">
                        <Form.Field className="item">
                          <label
                            style={{ fontWeight: 'bold', fontSize: '1.14rem' }}
                          >
                            Institution Name:{' '}
                            <span
                              style={{
                                color: colors.red,
                                fontSize: '1.28rem',
                                fontStyle: 'bold',
                              }}
                            >
                              {!isAccount && '*'}
                            </span>
                          </label>
                          {isAccount ? (
                            <Value>{institution}</Value>
                          ) : (
                            <Input
                              name="institution"
                              type="text"
                              fluid
                              value={institution}
                              onChange={this.handleChange}
                              error={!!errors.institution}
                              placeholder="e.g. Eames Direct Investing"
                            />
                          )}
                          <div className="error-label">
                            {errors.institution}
                          </div>
                        </Form.Field>
                        <Form.Field className="item">
                          <label
                            style={{ fontWeight: 'bold', fontSize: '1.14rem' }}
                          >
                            Account Type:{' '}
                            <Popup
                              trigger={
                                <Icon
                                  name="question circle outline"
                                  style={{
                                    fontSize: '15px',
                                    verticalAlign: 'initial',
                                    color: 'dimgrey',
                                  }}
                                />
                              }
                              position="right center"
                              hoverable
                              wide
                            >
                              e.g. Cash, TFSA, RRSP, etc.
                            </Popup>
                            <span
                              style={{
                                color: colors.red,
                                fontSize: '1.28rem',
                                fontStyle: 'bold',
                              }}
                            >
                              {!isAccount && '*'}
                            </span>
                          </label>
                          {isAccount ? (
                            <Value>{accountName}</Value>
                          ) : (
                            <Input
                              name="accountName"
                              type="text"
                              fluid
                              value={accountName}
                              onChange={this.handleChange}
                              error={!!errors.accountName}
                              placeholder="e.g. RRSP"
                            />
                          )}
                          <div className="error-label">
                            {errors.accountName}
                          </div>
                        </Form.Field>
                      </div>
                      <div className="column">
                        <Form.Field className="item">
                          <label
                            style={{ fontWeight: 'bold', fontSize: '1.14rem' }}
                          >
                            Account Number (no dashes):{' '}
                            <span
                              style={{
                                color: colors.red,
                                fontSize: '1.28rem',
                                fontStyle: 'bold',
                              }}
                            >
                              {((isAccount && account.provider !== 'uld') ||
                                !isAccount) &&
                                '*'}
                            </span>
                          </label>
                          {isAccount && account.provider === 'uld' ? (
                            <Value>{account.account_number}</Value>
                          ) : (
                            <Input
                              name="accountNumber"
                              type="text"
                              fluid
                              value={accountNumber}
                              onChange={this.handleChange}
                              error={!!errors.accountNumber}
                              placeholder="e.g. EDI123456"
                            />
                          )}
                          <div className="error-label">
                            {errors.accountNumber}
                          </div>
                        </Form.Field>
                        <Form.Field className="item">
                          <label
                            style={{
                              fontWeight: 'bold',
                              fontSize: '1.14rem',
                            }}
                          >
                            Account Currency:{' '}
                            <span
                              style={{
                                color: colors.red,
                                fontSize: '1.28rem',
                                fontStyle: 'bold',
                              }}
                            >
                              {!isAccount && '*'}
                            </span>
                          </label>
                          {isAccount ? (
                            <Value>{currency}</Value>
                          ) : (
                            <Dropdown
                              name="currency"
                              compact
                              placeholder="Select"
                              selection
                              options={[
                                {
                                  key: 'CAD',
                                  value: 'CAD',
                                  text: 'CAD',
                                },
                                { key: 'USD', value: 'USD', text: 'USD' },
                              ]}
                              value={currency}
                              onChange={this.handleChange}
                              className="currency"
                            />
                          )}
                        </Form.Field>
                      </div>
                    </FormGroup>
                    <Divider />

                    <Header
                      style={{
                        textAlign: 'left',
                        width: '100%',
                        marginBottom: '2rem',
                      }}
                    >
                      Upload Statement or Screenshot
                      <Header.Subheader>
                        Text-based PDF or PNG/JPG/JPEG image files. Max file
                        size 5MB.
                      </Header.Subheader>
                    </Header>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1rem',
                        marginBottom: '2rem',
                      }}
                    >
                      <div
                        style={{
                          border: '1px solid #dededf',
                          backgroundColor: '#f9fbfc',
                          borderRadius: '8px',
                          padding: '1rem',
                        }}
                      >
                        <Header>
                          PDFs
                          <Header.Subheader style={{ marginTop: '0.5rem' }}>
                            If you have an Excel file, print and save it as a
                            PDF first. Make sure it shows the account number(s)
                            and market values.
                          </Header.Subheader>
                        </Header>
                      </div>
                      <div
                        style={{
                          border: '1px solid #dededf',
                          backgroundColor: '#f9fbfc',
                          borderRadius: '8px',
                          padding: '1rem',
                        }}
                      >
                        <Header>
                          Screenshots
                          <Header.Subheader style={{ marginTop: '0.5rem' }}>
                            One account only. Include all holdings with ticker
                            symbols, quantities and market values.
                          </Header.Subheader>
                        </Header>
                      </div>
                    </div>
                    <FormGroup>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          textAlign: 'right',
                        }}
                      >
                        <FileSelectLarge
                          selectedFile={selectedFile}
                          setSelectedFile={this.setSelectedFile}
                          fileTypes=".pdf, .png, .jpg, .jpeg"
                        />
                        <a
                          href="https://forms.gle/GCmAFcYJ121YYJ2D8"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginRight: '2rem' }}
                        >
                          Help us improve the uploader →
                        </a>
                      </div>
                    </FormGroup>
                  </Content>
                  {pdfDown && (
                    <p
                      style={{
                        color: colors.red,
                        fontSize: '1rem',
                        maxWidth: '38rem',
                        alignSelf: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Icon name="warning sign" />
                      The PDF upload service is currently unavailable from
                      OpenAI. Please try again later or upload an image instead.
                    </p>
                  )}
                  <Popup
                    trigger={
                      <ButtonFilled
                        className={cn('left', {
                          disabled: this.isSubmitButtonDisabled(),
                        })}
                        color={colors.blue}
                        margin={pdfDown ? '0' : '1rem 0'}
                        onClick={() => this.submit()}
                        style={{ alignSelf: 'end', marginRight: '2rem' }}
                      >
                        Submit
                      </ButtonFilled>
                    }
                    position="right center"
                  >
                    Check that your loaded file matches your account details
                    before submitting to avoid errors.
                  </Popup>
                </div>
              </Fade>
            </StyledDiv>
          </div>
        </Segment>
      </div>
    );
  }
}

UploadStatement.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  account: PropTypes.object,
  pollingStatus: PropTypes.string,
  pdfDown: PropTypes.bool,
};

UploadStatement.defaultProps = {
  history: {},
  loading: false,
  error: '',
  account: {},
  pollingStatus: 'idle',
  pdfDown: false,
};

export default withRouter(
  connect((state) => ({
    loading: state.AiTools.loading,
    error: state.AiTools.error,
    pollingStatus: state.AiTools.pollingStatus,
    pdfDown: state.AiTools.pdfDown,
  }))(UploadStatement)
);

const StyledDiv = styled.div`
  & {
    width: 100%;
    height: 100%;
    display: flex;
    padding: 1rem 0 0 0;
    flex-direction: column;
    justify-content: center;
    & > div {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    & .button {
      display: flex;
      align-items: end;
      justify-content: center;
    }
  }
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  min-height: 60vh;
`;

const FormGroup = styled.form`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  margin-left: auto;
  margin-right: auto;

  & .column {
    display: flex;
    flex-direction: column;
    row-gap: 0.7rem;
    width: 49%;
    align-items: start;
    text-align: left;

    & .item {
      display: flex;
      flex-direction: column;
      row-gap: 0.3rem;
      width: 100%;

      & .error-label {
        color: ${colors.red};
        height: 12px;
      }

      & .currency {
        width: 100%;
      }
    }
  }
`;

const CollapseWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: ${(props) => (props.open ? '0rem 1rem 0' : '0.5rem 1rem 0')};
  margin: 1rem 2rem 2rem 0;
  border: ${(props) =>
    props.open ? `1px solid ${colors.blue}` : '1px solid #dededf'};
  border-radius: 8px;
  overflow: ${(props) => (props.open ? 'visible' : 'hidden')};
  max-height: ${(props) => (props.open ? '2000px' : '45px')};
  transition: max-height 300ms ease, padding 300ms ease, border 300ms ease 300ms;
  cursor: pointer;
  background-color: ${(props) => (props.open ? `transparent` : '#f9fbfc;')};
`;

const Assumptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  text-align: left;
  padding-bottom: 10px;
  color: rgba(0, 0, 0, 0.6);

  .header {
    color: rgba(0, 0, 0, 0.7);
  }
  & > ul {
    & > li {
      margin: 10px 0;
    }
  }
`;

const StyledButton = styled.button`
  background-color: transparent;
  border: 2px solid ${colors.blue};
  color: ${colors.blue};
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  font-weight: bold;
  padding: 0;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 6px;

  &:hover {
    background-color: ${colors.hintBlue};
  }

  &:focus {
    outline: none;
  }
`;
const Value = styled.div`
  font-size: 17px;
  color: ${colors.grey};
  line-height: 37px;
`;

const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  column-gap: 1rem;
  .chip {
    display: flex;
    align-items: center;
    background-color: ${colors.lightOrange};
    border-radius: 15px;
    padding: 5px 10px 5px 10px;
  }
  .message {
    border: 1px solid ${colors.darkOrange};
    border-radius: 10px;
    background-color: ${colors.lighterOrange};
    text-align: center;
    padding: 15px;
  }
`;
